import { Text } from "@react-three/drei";
import { useGameStore } from "../game/useGameStore";
import {
  SURVIVAL_MISS_LIMIT,
  SURVIVAL_TRACKS,
  normalizedIntensity,
  waveNumber,
} from "../game/survival";
import { colors } from "./theme";

export function HUD() {
  const score = useGameStore((s) => s.score);
  const mode = useGameStore((s) => s.mode);
  const lastRunThreshold = useGameStore((s) => s.lastRunThreshold);
  const survivalBest = useGameStore((s) => s.survivalBest);
  const missCount = useGameStore((s) => s.missCount);
  const survivalElapsed = useGameStore((s) => s.survivalElapsed);
  const survivalTrackIndex = useGameStore((s) => s.survivalTrackIndex);
  const survivalIntensity = useGameStore((s) => s.survivalIntensity);

  const scoreText = score.toString().padStart(6, "0");
  const thresholdPct =
    mode === "campaign"
      ? Math.min(1, score / Math.max(1, lastRunThreshold))
      : 0;
  const thresholdText = lastRunThreshold.toLocaleString("en-US");
  const sessionHigh = Math.max(survivalBest, score);
  const survivalTrack =
    SURVIVAL_TRACKS[survivalTrackIndex] ?? SURVIVAL_TRACKS[0];
  const intensityPct = Math.round(normalizedIntensity(survivalIntensity) * 100);

  return (
    <>
      <group position={[-1.15, 2.36, -1.9]}>
        <mesh position={[0.22, -0.055, -0.03]} renderOrder={1000}>
          <planeGeometry args={[0.54, 0.32]} />
          <meshBasicMaterial
            color={colors.ink}
            transparent
            opacity={0.76}
            depthTest={false}
            depthWrite={false}
          />
        </mesh>
        {/* Score and threshold live in peripheral vision, away from the cut line. */}
        <Text
          position={[0, 0.045, 0]}
          fontSize={0.082}
          color={colors.washi}
          anchorX="left"
          anchorY="middle"
          letterSpacing={0.05}
          outlineWidth={0.003}
          outlineColor={colors.ink}
          renderOrder={1001}
          material-depthTest={false}
        >
          {`SCORE\n${scoreText}`}
        </Text>

        {mode === "campaign" && (
          <group position={[0, -0.12, 0]}>
            <Text
              position={[0, 0.055, 0]}
              fontSize={0.032}
              color={colors.steel}
              anchorX="left"
              anchorY="middle"
              letterSpacing={0.04}
              outlineWidth={0.002}
              outlineColor={colors.ink}
              renderOrder={1001}
              material-depthTest={false}
            >
              {`THRESHOLD ${thresholdText}`}
            </Text>
            <mesh position={[0.18, -0.02, -0.002]} renderOrder={1001}>
              <planeGeometry args={[0.36, 0.018]} />
              <meshBasicMaterial
                color={colors.wood}
                transparent
                opacity={0.85}
                depthTest={false}
                depthWrite={false}
              />
            </mesh>
            <mesh
              position={[(0.36 * thresholdPct) / 2, -0.02, 0]}
              renderOrder={1002}
            >
              <planeGeometry args={[0.36 * thresholdPct, 0.018]} />
              <meshBasicMaterial
                color={colors.amber}
                transparent
                opacity={0.95}
                depthTest={false}
                depthWrite={false}
              />
            </mesh>
            <mesh position={[0.36, -0.02, 0.001]} renderOrder={1003}>
              <planeGeometry args={[0.006, 0.034]} />
              <meshBasicMaterial
                color={colors.steel}
                transparent
                opacity={0.75}
                depthTest={false}
                depthWrite={false}
              />
            </mesh>
          </group>
        )}
      </group>

      {mode === "survival" && (
        <group position={[0.72, 2.36, -1.9]}>
          <mesh position={[0.22, -0.045, -0.03]} renderOrder={1000}>
            <planeGeometry args={[0.82, 0.38]} />
            <meshBasicMaterial
              color={colors.ink}
              transparent
              opacity={0.78}
              depthTest={false}
              depthWrite={false}
            />
          </mesh>
          <Text
            position={[-0.16, 0.09, 0]}
            fontSize={0.05}
            color={
              missCount >= SURVIVAL_MISS_LIMIT - 1 ? colors.gold : colors.washi
            }
            anchorX="left"
            anchorY="middle"
            outlineWidth={0.002}
            outlineColor={colors.ink}
            renderOrder={1001}
            material-depthTest={false}
          >
            {`MISS ${missCount} / ${SURVIVAL_MISS_LIMIT}`}
          </Text>
          <Text
            position={[-0.16, 0.0, 0]}
            fontSize={0.038}
            color={colors.steel}
            anchorX="left"
            anchorY="middle"
            outlineWidth={0.002}
            outlineColor={colors.ink}
            renderOrder={1001}
            material-depthTest={false}
          >
            {`BEST ${sessionHigh.toLocaleString("en-US")}  WAVE ${waveNumber(survivalElapsed)}`}
          </Text>
          <Text
            position={[-0.16, -0.085, 0]}
            fontSize={0.032}
            maxWidth={0.72}
            color={colors.border}
            anchorX="left"
            anchorY="middle"
            outlineWidth={0.002}
            outlineColor={colors.ink}
            renderOrder={1001}
            material-depthTest={false}
          >
            {`${survivalTrack.title.toUpperCase()}  ${survivalTrack.rhythm.bpm} BPM`}
          </Text>
          <mesh position={[0.2, -0.15, 0]} renderOrder={1001}>
            <planeGeometry args={[0.72, 0.014]} />
            <meshBasicMaterial
              color={colors.wood}
              transparent
              opacity={0.9}
              depthTest={false}
              depthWrite={false}
            />
          </mesh>
          <mesh
            position={[-0.16 + (0.72 * intensityPct) / 200, -0.15, 0.001]}
            renderOrder={1002}
          >
            <planeGeometry args={[(0.72 * intensityPct) / 100, 0.014]} />
            <meshBasicMaterial
              color={colors.amber}
              transparent
              opacity={0.92}
              depthTest={false}
              depthWrite={false}
            />
          </mesh>
        </group>
      )}
    </>
  );
}
